import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router';
import { StandupNote, Task, Epic, Project, TimeLog } from '../types';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Megaphone,
  Plus,
  Search,
  Trash2,
  CalendarDays,
  Clock,
  User,
  Tag,
  GitBranch,
} from 'lucide-react';
import { useApp } from '../providers/AppProvider';
import { useOrganizationData } from '../hooks/useOrganizationData';
import { addStandupNote, deleteStandupNote } from '../services/dataService';
import { formatDistanceToNow, format, isToday, isYesterday, parseISO, startOfDay, endOfDay, startOfWeek, endOfWeek, subWeeks, subDays } from 'date-fns';
import { APP_FILTER_LABEL_CLASS } from '../constants/formControls';
import { WIDGET_STATUS_BADGE_INFO_CLASS, WIDGET_STATUS_BADGE_SUCCESS_CLASS, WIDGET_STATUS_BADGE_PURPLE_CLASS } from '../constants/widgetTheme';

type TimeFrame = 'today' | 'yesterday' | 'this_week' | 'last_week' | 'last_7' | 'last_30' | 'all';

const TIMEFRAME_LABELS: Record<TimeFrame, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  this_week: 'This week',
  last_week: 'Last week',
  last_7: 'Last 7 days',
  last_30: 'Last 30 days',
  all: 'All time',
};

const SOURCE_BADGE: Record<string, string> = {
  manual: 'border-border bg-muted/40 text-muted-foreground',
  task_assigned: WIDGET_STATUS_BADGE_INFO_CLASS,
  task_completed: WIDGET_STATUS_BADGE_SUCCESS_CLASS,
  comment: WIDGET_STATUS_BADGE_PURPLE_CLASS,
};
const SOURCE_LABEL: Record<string, string> = {
  manual: 'note',
  task_assigned: 'assigned',
  task_completed: 'completed',
  comment: 'comment',
};

function getTimeFrameBounds(tf: TimeFrame): { from: Date; to: Date } {
  const now = new Date();
  switch (tf) {
    case 'today':
      return { from: startOfDay(now), to: endOfDay(now) };
    case 'yesterday': {
      const y = subDays(now, 1);
      return { from: startOfDay(y), to: endOfDay(y) };
    }
    case 'this_week':
      return { from: startOfWeek(now, { weekStartsOn: 1 }), to: endOfDay(now) };
    case 'last_week': {
      const lw = subWeeks(now, 1);
      return { from: startOfWeek(lw, { weekStartsOn: 1 }), to: endOfWeek(lw, { weekStartsOn: 1 }) };
    }
    case 'last_7':
      return { from: startOfDay(subDays(now, 6)), to: endOfDay(now) };
    case 'last_30':
      return { from: startOfDay(subDays(now, 29)), to: endOfDay(now) };
    default:
      return { from: new Date(0), to: endOfDay(now) };
  }
}

function formatNoteDate(dateStr: string): string {
  try {
    const d = parseISO(dateStr);
    if (isToday(d)) return 'Today';
    if (isYesterday(d)) return 'Yesterday';
    return format(d, 'EEE, MMM d');
  } catch {
    return dateStr.slice(0, 10);
  }
}

function groupByDate(notes: StandupNote[]): [string, StandupNote[]][] {
  const groups: Record<string, StandupNote[]> = {};
  for (const note of notes) {
    const key = note.createdAt.slice(0, 10);
    (groups[key] ??= []).push(note);
  }
  return Object.entries(groups).sort(([a], [b]) => b.localeCompare(a));
}

function groupTimeLogsByDate(logs: Array<TimeLog & { task: Task }>): [string, Array<TimeLog & { task: Task }>][] {
  const groups: Record<string, Array<TimeLog & { task: Task }>> = {};
  for (const log of logs) {
    const key = (log.date ?? log.task.updatedAt ?? '').slice(0, 10);
    (groups[key] ??= []).push(log);
  }
  return Object.entries(groups).sort(([a], [b]) => b.localeCompare(a));
}

export default function StandupPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { state, reload } = useApp();
  const { projects, tasks, epics } = useOrganizationData(state);

  const [timeFrame, setTimeFrame] = useState<TimeFrame>('this_week');
  const [searchQuery, setSearchQuery] = useState('');
  const [authorFilter, setAuthorFilter] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'standup' | 'timelog'>('standup');
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ content: '', taskId: '', epicId: '' });
  const [deleteTarget, setDeleteTarget] = useState<StandupNote | null>(null);
  const [saving, setSaving] = useState(false);


  const project = useMemo(
    () =>
      projects.find(
        (p: Project) => String(p.id).toLowerCase() === String(projectId).toLowerCase()
      ),
    [projects, projectId]
  );

  const projectTasks = useMemo(
    () => tasks.filter((t: Task) => t.projectId === projectId),
    [tasks, projectId]
  );

  const projectEpics = useMemo(
    () => epics.filter((e: Epic) => e.projectId === projectId),
    [epics, projectId]
  );

  // Collect all standup notes for this project from global state + embedded in tasks
  const allStandupNotes = useMemo(() => {
    const seen = new Set<string>();
    const notes: StandupNote[] = [];

    // From global standupNotes list
    for (const note of state?.standupNotes ?? []) {
      if (note.projectId === projectId && !seen.has(note.id)) {
        seen.add(note.id);
        notes.push(note);
      }
    }
    // Also pick up embedded notes on tasks for this project
    for (const task of projectTasks) {
      for (const note of task.standupNotes ?? []) {
        if (!seen.has(note.id)) {
          seen.add(note.id);
          notes.push({ ...note, projectId, taskId: note.taskId ?? task.id });
        }
      }
    }
    // And embedded notes on epics
    for (const epic of projectEpics) {
      for (const note of (epic as any).standupNotes ?? []) {
        if (!seen.has(note.id)) {
          seen.add(note.id);
          notes.push({ ...note, projectId, epicId: note.epicId ?? epic.id });
        }
      }
    }

    return notes.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [state?.standupNotes, projectTasks, projectEpics, projectId]);

  // All time logs across project tasks
  const allTimeLogs = useMemo(() => {
    const logs: Array<TimeLog & { task: Task }> = [];
    for (const task of projectTasks) {
      for (const log of task.timeLogs ?? []) {
        logs.push({ ...log, task });
      }
    }
    return logs.sort(
      (a, b) =>
        new Date(b.date ?? b.task.updatedAt).getTime() -
        new Date(a.date ?? a.task.updatedAt).getTime()
    );
  }, [projectTasks]);

  // Unique authors for filter
  const authors = useMemo(() => {
    const map = new Map<string, string>();
    for (const note of allStandupNotes) {
      const name =
        note.authorName ??
        state?.users?.find(u => u.id === note.authorId)?.name ??
        note.authorId;
      map.set(note.authorId, name);
    }
    return Array.from(map.entries());
  }, [allStandupNotes, state?.users]);

  const { from: tfFrom, to: tfTo } = useMemo(() => getTimeFrameBounds(timeFrame), [timeFrame]);

  const filtered = useMemo(() => {
    return allStandupNotes.filter(note => {
      try {
        const d = parseISO(note.createdAt);
        if (d < tfFrom || d > tfTo) return false;
      } catch { return false; }
      if (searchQuery && !note.content.toLowerCase().includes(searchQuery.toLowerCase()))
        return false;
      if (authorFilter !== 'all' && note.authorId !== authorFilter) return false;
      return true;
    });
  }, [allStandupNotes, searchQuery, authorFilter, tfFrom, tfTo]);

  const grouped = useMemo(() => groupByDate(filtered), [filtered]);

  const filteredTimeLogs = useMemo(() => {
    return allTimeLogs.filter(log => {
      try {
        const d = parseISO(log.date ?? log.task.updatedAt ?? '');
        if (d < tfFrom || d > tfTo) return false;
      } catch { return false; }
      if (authorFilter !== 'all' && log.userId !== authorFilter) return false;
      return true;
    });
  }, [allTimeLogs, authorFilter, tfFrom, tfTo]);

  const totalHours = useMemo(
    () => filteredTimeLogs.reduce((sum, l) => sum + (l.hours ?? 0), 0),
    [filteredTimeLogs]
  );

  const timeLogsByUser = useMemo(() => {
    const map = new Map<string, { name: string; hours: number; entries: typeof filteredTimeLogs }>();
    for (const log of filteredTimeLogs) {
      const user = state?.users?.find(u => u.id === log.userId);
      const name = user?.name ?? user?.email ?? log.userId;
      if (!map.has(log.userId)) map.set(log.userId, { name, hours: 0, entries: [] });
      const entry = map.get(log.userId)!;
      entry.hours += log.hours ?? 0;
      entry.entries.push(log);
    }
    return Array.from(map.values()).sort((a, b) => b.hours - a.hours);
  }, [filteredTimeLogs, state?.users]);

  const groupedTimeLogs = useMemo(() => groupTimeLogsByDate(filteredTimeLogs), [filteredTimeLogs]);

  const handleAddNote = async () => {
    if (!project || !form.content.trim()) return;
    setSaving(true);
    try {
      const currentUser = state?.currentUser;
      const now = new Date().toISOString();
      const note: StandupNote = {
        id: `standup-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        organizationId: project.organizationId,
        projectId: project.id,
        authorId: currentUser?.id ?? '',
        authorName: currentUser?.name ?? currentUser?.email ?? 'Unknown',
        content: form.content.trim(),
        createdAt: now,
        taskId: form.taskId || undefined,
        taskTitle: form.taskId
          ? projectTasks.find((t: Task) => t.id === form.taskId)?.title
          : undefined,
        epicId: form.epicId || undefined,
        source: 'manual',
      };
      await addStandupNote(note);
      await reload(state?.currentUser?.email ?? '');
      setForm({ content: '', taskId: '', epicId: '' });
      setShowDialog(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteStandupNote(deleteTarget.id);
    await reload(state?.currentUser?.email ?? '');
    setDeleteTarget(null);
  };

  if (!project) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground mb-4">Project not found</p>
        <Link to="/app/projects" className="text-primary hover:underline">
          Back to Projects
        </Link>
      </div>
    );
  }

  // ── Shared filter bar ──────────────────────────────────────────────────────
  const filterBar = (
    <div className="flex flex-wrap gap-2 items-center">
      {/* Time-frame buttons */}
      <div className="flex gap-1 flex-wrap">
        {(Object.keys(TIMEFRAME_LABELS) as TimeFrame[]).map(tf => (
          <button
            key={tf}
            onClick={() => setTimeFrame(tf)}
            className={`px-3 py-1 text-xs rounded-full font-medium border transition-colors ${
              timeFrame === tf
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-background text-muted-foreground hover:border-chart-4/40 hover:text-chart-4'
            }`}
          >
            {TIMEFRAME_LABELS[tf]}
          </button>
        ))}
      </div>
      <div className="ml-auto flex flex-wrap items-end gap-2">
        {authors.length > 1 && (
          <div className="w-36 space-y-1.5">
            <span className={APP_FILTER_LABEL_CLASS}>Member</span>
            <Select value={authorFilter} onValueChange={setAuthorFilter}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="All members" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All members</SelectItem>
                {authors.map(([id, name]) => (
                  <SelectItem key={id} value={id}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="w-40 space-y-1.5">
          <span className={APP_FILTER_LABEL_CLASS}>Search</span>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="h-8 w-40 pl-8 text-xs"
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-3 lg:pt-4 pb-6 max-w-7xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Megaphone className="h-5 w-5 text-chart-4" />
          <div>
            <h1 className="text-xl font-bold text-muted-foreground">Standup & Time Log</h1>
            <p className="text-xs text-muted-foreground">{project.name}</p>
          </div>
        </div>
        {activeTab === 'standup' && (
          <Button onClick={() => setShowDialog(true)} size="sm" className="gap-1.5">
            <Plus className="h-3.5 w-3.5" />
            Add Note
          </Button>
        )}
      </div>

      {/* Tab bar */}
      <div className="flex gap-0 border-border">
        {(['standup', 'timelog'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-chart-4/40 text-chart-4'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab === 'standup' ? (
              <span className="flex items-center gap-1.5">
                <Megaphone className="h-3.5 w-3.5" />
                Standup
                <span className="rounded-full bg-muted/40 px-1.5 py-0.5 text-xs text-muted-foreground">
                  {filtered.length}
                </span>
              </span>
            ) : (
              <span className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                Time Log
                {totalHours > 0 && (
                  <span className="rounded-full bg-muted/40 px-1.5 py-0.5 text-xs text-muted-foreground">
                    {totalHours.toFixed(1)}h
                  </span>
                )}
              </span>
            )}
          </button>
        ))}
      </div>

      {filterBar}

      {/* ── STANDUP TAB ── */}
      {activeTab === 'standup' && (
        <>
          {grouped.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <Megaphone className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
              <p className="text-sm">
                {allStandupNotes.length === 0
                  ? 'No standup notes yet.'
                  : `No notes for ${TIMEFRAME_LABELS[timeFrame].toLowerCase()}.`}
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              {grouped.map(([dateKey, dayNotes]) => (
                <div key={dateKey}>
                  {/* Date header */}
                  <div className="flex items-center gap-2 mb-1.5">
                    <CalendarDays className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {formatNoteDate(dayNotes[0].createdAt)}
                    </span>
                    <hr className="flex-1 border-border" />
                    <span className="text-xs text-muted-foreground">{dayNotes.length}</span>
                  </div>

                  {/* Compact rows */}
                  <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-muted/40">
                    {dayNotes.map(note => {
                      const author =
                        note.authorName ??
                        state?.users?.find(u => u.id === note.authorId)?.name ??
                        note.authorId;
                      const source = note.source ?? 'manual';
                      const isOwnNote = note.authorId === state?.currentUser?.id;
                      const linkedTask = note.taskId
                        ? projectTasks.find((t: Task) => t.id === note.taskId)
                        : null;
                      const linkedEpic = note.epicId
                        ? projectEpics.find((e: Epic) => e.id === note.epicId)
                        : null;
                      return (
                        <div
                          key={note.id}
                          className="group flex items-start gap-3 px-3 py-2.5 hover:bg-muted/40"
                        >
                          {/* Time */}
                          <span className="w-12 shrink-0 pt-0.5 text-right text-xs text-muted-foreground">
                            {format(parseISO(note.createdAt), 'HH:mm')}
                          </span>

                          {/* Author */}
                          <span className="w-24 shrink-0 truncate pt-0.5 text-xs font-medium text-muted-foreground">
                            {author}
                          </span>

                          {/* Source badge */}
                          <span
                             className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 font-medium mt-0.5 border ${SOURCE_BADGE[source] ?? 'border-border bg-muted/40 text-muted-foreground'}`}
                          >
                            {SOURCE_LABEL[source] ?? source}
                          </span>

                          {/* Content + optional task/epic link */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm leading-snug text-muted-foreground">
                              {note.content}
                            </p>
                            {(linkedTask || linkedEpic) && (
                              <div className="flex gap-2 mt-0.5">
                                {linkedTask && (
                                  <span className="inline-flex items-center gap-1 text-[10px] text-primary">
                                    <Tag className="h-2.5 w-2.5" />
                                    {linkedTask.title}
                                  </span>
                                )}
                                {linkedEpic && (
                                  <span className="inline-flex items-center gap-1 text-[10px] text-chart-4">
                                    <GitBranch className="h-2.5 w-2.5" />
                                    {linkedEpic.title}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>

                          {isOwnNote && (
                            <button
                              title="Delete note"
                              onClick={() => setDeleteTarget(note)}
                              className="opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive/80 transition-opacity shrink-0"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── TIME LOG TAB ── */}
      {activeTab === 'timelog' && (
        <>
          {/* Summary row */}
          <div className="flex gap-4 border-b border-border pb-3 text-sm text-muted-foreground">
            <span>
              <span className="font-bold text-muted-foreground">{totalHours.toFixed(1)}h</span> logged
            </span>
            <span>
              <span className="font-bold text-muted-foreground">{filteredTimeLogs.length}</span> entries
            </span>
            <span>
              <span className="font-bold text-muted-foreground">{timeLogsByUser.length}</span> contributors
            </span>
            <span className="ml-auto text-xs text-muted-foreground">
              vs {projectTasks.reduce((s, t) => s + (t.estimatedHours ?? 0), 0).toFixed(0)}h estimated
            </span>
          </div>

          {filteredTimeLogs.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <Clock className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
              <p className="text-sm">No time logged for {TIMEFRAME_LABELS[timeFrame].toLowerCase()}.</p>
            </div>
          ) : (
            <div className="space-y-5">
              {groupedTimeLogs.map(([dateKey, dayLogs]) => {
                const dayTotal = dayLogs.reduce((s, l) => s + (l.hours ?? 0), 0);
                return (
                  <div key={dateKey}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <CalendarDays className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {formatNoteDate(dayLogs[0].date ?? dayLogs[0].task.updatedAt ?? dateKey)}
                      </span>
                      <hr className="flex-1 border-border" />
                      <span className="text-xs font-semibold text-chart-2">{dayTotal.toFixed(1)}h</span>
                    </div>

                    <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-muted/40">
                      {dayLogs.map(log => {
                        const user = state?.users?.find(u => u.id === log.userId);
                        const userName = user?.name ?? user?.email ?? log.userId;
                        return (
                          <div
                            key={log.id}
                            className="flex items-center gap-3 px-3 py-2 hover:bg-muted/40"
                          >
                            <span className="w-12 shrink-0 text-right text-xs text-muted-foreground">
                              {log.date ? format(parseISO(log.date), 'HH:mm') : '—'}
                            </span>
                            <span className="w-24 shrink-0 truncate text-xs font-medium text-muted-foreground">
                              {userName}
                            </span>
                            <span className="flex-1 truncate text-sm text-muted-foreground">
                              {log.task.title}
                            </span>
                            {log.description && (
                              <span className="hidden max-w-[180px] truncate text-xs text-muted-foreground sm:block">
                                {log.description}
                              </span>
                            )}
                            <span className="text-sm font-semibold text-chart-2 shrink-0 w-10 text-right">
                              {log.hours}h
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Add Standup Note Dialog */}
      <Dialog
        open={showDialog}
        onOpenChange={open => {
          if (!open) {
            setShowDialog(false);
            setForm({ content: '', taskId: '', epicId: '' });
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Standup Note</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label>Note *</Label>
              <Textarea
                value={form.content}
                onChange={e => setForm(p => ({ ...p, content: e.target.value }))}
                placeholder="What did you work on? Any blockers? What's next?"
                className="min-h-[80px]"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Link to Task</Label>
                <Select
                  value={form.taskId || 'none'}
                  onValueChange={v => setForm(p => ({ ...p, taskId: v === 'none' ? '' : v }))}
                >
                  <SelectTrigger className="text-xs">
                    <SelectValue placeholder="Optional" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    <SelectItem value="none">None</SelectItem>
                    {projectTasks.map((t: Task) => (
                      <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Link to Epic</Label>
                <Select
                  value={form.epicId || 'none'}
                  onValueChange={v => setForm(p => ({ ...p, epicId: v === 'none' ? '' : v }))}
                >
                  <SelectTrigger className="text-xs">
                    <SelectValue placeholder="Optional" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {projectEpics.map((e: Epic) => (
                      <SelectItem key={e.id} value={e.id}>{e.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleAddNote} disabled={saving || !form.content.trim()}>
              {saving ? 'Saving...' : 'Add Note'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={open => { if (!open) setDeleteTarget(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Standup Note</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this standup note? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
